<script setup>
import { ref, onMounted } from 'vue';

// Instagram Auth Status entfernt
const loading = ref(false);
const error = ref(null);
const successMessage = ref(null);

// Allgemeine Einstellungen
const siteSettings = ref({
  maintenance_mode: false,
  show_fan_section: true,
  site_title: '',
  contact_email: '',
  contact_phone: '',
  contact_location: '',
  footer_text: '',
  facebook_url: '',
  youtube_url: '',
  tiktok_url: '',
  instagram_url: '',
  hero_image: '',
  logo_url: '',
  favicon_url: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: ''
});

// Neue Variablen für Bild-Upload
const selectedHeroImage = ref(null);
const heroImagePreview = ref('');
const selectedLogoImage = ref(null);
const logoImagePreview = ref('');
const selectedFaviconImage = ref(null);
const faviconImagePreview = ref('');
const isUploading = ref(false);

// Einstellungen laden
const loadSettings = async () => {
  try {
    loading.value = true;
    const response = await fetch('/api/settings.php');
    const data = await response.json();
    
    if (data.success && data.settings) {
      // Boolean-Werte korrekt konvertieren
      siteSettings.value = {
        maintenance_mode: data.settings.maintenance_mode === '1',
        show_fan_section: data.settings.show_fan_section === '1',
        site_title: data.settings.site_title || '',
        contact_email: data.settings.contact_email || '',
        contact_phone: data.settings.contact_phone || '+49 123 456 7890',
        contact_location: data.settings.contact_location || 'München, Deutschland',
        footer_text: data.settings.footer_text || '',
        facebook_url: data.settings.facebook_url || '',
        youtube_url: data.settings.youtube_url || '',
        tiktok_url: data.settings.tiktok_url || '',
        instagram_url: data.settings.instagram_url || '',
        hero_image: data.settings.hero_image || '',
        logo_url: data.settings.logo_url || '',
        favicon_url: data.settings.favicon_url || '',
        meta_title: data.settings.meta_title || '',
        meta_description: data.settings.meta_description || '',
        meta_keywords: data.settings.meta_keywords || ''
      };
    }
  } catch (err) {
    console.error('Fehler beim Laden der Einstellungen:', err);
    error.value = 'Die Einstellungen konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
};

// Einstellungen speichern
const saveSettings = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    // Werte für API formatieren
    const settingsToSave = {
      maintenance_mode: siteSettings.value.maintenance_mode ? '1' : '0',
      show_fan_section: siteSettings.value.show_fan_section ? '1' : '0',
      site_title: siteSettings.value.site_title,
      contact_email: siteSettings.value.contact_email,
      contact_phone: siteSettings.value.contact_phone,
      contact_location: siteSettings.value.contact_location,
      footer_text: siteSettings.value.footer_text,
      facebook_url: siteSettings.value.facebook_url,
      youtube_url: siteSettings.value.youtube_url,
      tiktok_url: siteSettings.value.tiktok_url,
      instagram_url: siteSettings.value.instagram_url,
      hero_image: siteSettings.value.hero_image,
      logo_url: siteSettings.value.logo_url,
      favicon_url: siteSettings.value.favicon_url,
      meta_title: siteSettings.value.meta_title,
      meta_description: siteSettings.value.meta_description,
      meta_keywords: siteSettings.value.meta_keywords
    };
    
    const response = await fetch('/api/settings.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settingsToSave)
    });
    
    const data = await response.json();
    
    if (data.success) {
      successMessage.value = 'Einstellungen wurden erfolgreich gespeichert.';
      setTimeout(() => {
        successMessage.value = null;
      }, 5000);
    } else {
      throw new Error(data.error || 'Unbekannter Fehler');
    }
  } catch (err) {
    console.error('Fehler beim Speichern der Einstellungen:', err);
    error.value = 'Die Einstellungen konnten nicht gespeichert werden.';
  } finally {
    loading.value = false;
  }
};

// Bild-Upload-Handler
const handleHeroImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedHeroImage.value = file;
    heroImagePreview.value = URL.createObjectURL(file);
  }
};

// Hero-Bild hochladen
const uploadHeroImage = async () => {
  if (!selectedHeroImage.value) return;
  
  isUploading.value = true;
  error.value = null;
  
  try {
    const formData = new FormData();
    formData.append('image', selectedHeroImage.value);
    formData.append('type', 'hero');
    
    const response = await fetch('/api/upload.php', {
      method: 'POST',
      body: formData
    });
    
    const data = await response.json();
    
    if (data.success) {
      siteSettings.value.hero_image = data.imageUrl;
      // Einstellungen nach erfolgreichem Upload speichern
      await saveSettings();
      successMessage.value = 'Hero-Bild erfolgreich hochgeladen!';
      setTimeout(() => {
        successMessage.value = null;
      }, 5000);
    } else {
      throw new Error(data.error || 'Fehler beim Hochladen des Bildes');
    }
  } catch (err) {
    console.error('Fehler beim Hochladen des Hero-Bildes:', err);
    error.value = 'Das Hero-Bild konnte nicht hochgeladen werden.';
  } finally {
    isUploading.value = false;
  }
};

// Logo-Upload-Handler
const handleLogoImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedLogoImage.value = file;
    logoImagePreview.value = URL.createObjectURL(file);
  }
};

// Logo hochladen
const uploadLogoImage = async () => {
  if (!selectedLogoImage.value) return;
  
  isUploading.value = true;
  error.value = null;
  
  try {
    const formData = new FormData();
    formData.append('image', selectedLogoImage.value);
    formData.append('type', 'logo');
    
    const response = await fetch('/api/upload.php', {
      method: 'POST',
      body: formData
    });
    
    const data = await response.json();
    
    if (data.success) {
      siteSettings.value.logo_url = data.imageUrl;
      // Einstellungen nach erfolgreichem Upload speichern
      await saveSettings();
      successMessage.value = 'Logo erfolgreich hochgeladen!';
      setTimeout(() => {
        successMessage.value = null;
      }, 5000);
    } else {
      throw new Error(data.error || 'Fehler beim Hochladen des Bildes');
    }
  } catch (err) {
    console.error('Fehler beim Hochladen des Logos:', err);
    error.value = 'Das Logo konnte nicht hochgeladen werden.';
  } finally {
    isUploading.value = false;
  }
};

// Favicon-Upload-Handler
const handleFaviconImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedFaviconImage.value = file;
    faviconImagePreview.value = URL.createObjectURL(file);
  }
};

// Favicon hochladen
const uploadFaviconImage = async () => {
  if (!selectedFaviconImage.value) return;
  
  isUploading.value = true;
  error.value = null;
  
  try {
    const formData = new FormData();
    formData.append('image', selectedFaviconImage.value);
    formData.append('type', 'favicon');
    
    const response = await fetch('/api/upload.php', {
      method: 'POST',
      body: formData
    });
    
    const data = await response.json();
    
    if (data.success) {
      siteSettings.value.favicon_url = data.imageUrl;
      // Einstellungen nach erfolgreichem Upload speichern
      await saveSettings();
      successMessage.value = 'Favicon erfolgreich hochgeladen!';
      setTimeout(() => {
        successMessage.value = null;
      }, 5000);
    } else {
      throw new Error(data.error || 'Fehler beim Hochladen des Bildes');
    }
  } catch (err) {
    console.error('Fehler beim Hochladen des Favicons:', err);
    error.value = 'Das Favicon konnte nicht hochgeladen werden.';
  } finally {
    isUploading.value = false;
  }
};

// Bei Komponentenaufbau Einstellungen laden
onMounted(() => {
  loadSettings();
});
</script>

<template>
  <div class="settings-manager">
    <div class="section-header">
      <h2>Einstellungen</h2>
      <p class="section-description">
        Hier kannst du verschiedene Einstellungen für deine Website verwalten.
      </p>
    </div>
    
    <!-- Allgemeine Einstellungen -->
    <div class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </div>
        <div>
          <h3>Allgemeine Einstellungen</h3>
          <p>Grundlegende Einstellungen für deine Website.</p>
        </div>
      </div>
      
      <form @submit.prevent="saveSettings" class="settings-form">
        <div class="form-group">
          <div class="form-toggle">
            <label for="maintenance-mode">
              <span>Wartungsmodus</span>
              <div class="toggle-description">
                Wenn aktiviert, wird die Website für Besucher als "In Wartung" angezeigt.
              </div>
            </label>
            <div class="toggle-switch">
              <input
                type="checkbox"
                id="maintenance-mode"
                v-model="siteSettings.maintenance_mode"
              />
              <label for="maintenance-mode" class="toggle-label"></label>
            </div>
          </div>
        </div>
        
        <div class="form-group">
          <div class="form-toggle">
            <label for="fan-section">
              <span>Fan-Bereich anzeigen</span>
              <div class="toggle-description">
                Wenn aktiviert, wird der Fan-Bereich mit Fan-Fotos und Upload-Möglichkeit auf der Website angezeigt.
              </div>
            </label>
            <div class="toggle-switch">
              <input
                type="checkbox"
                id="fan-section"
                v-model="siteSettings.show_fan_section"
              />
              <label for="fan-section" class="toggle-label"></label>
            </div>
          </div>
        </div>
        
        <div class="form-group">
          <label for="site-title">Website-Titel</label>
          <input
            type="text"
            id="site-title"
            v-model="siteSettings.site_title"
            placeholder="z.B. HomeWard - Irish Folk Band"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="contact-email">Kontakt-E-Mail</label>
          <input
            type="email"
            id="contact-email"
            v-model="siteSettings.contact_email"
            placeholder="z.B. kontakt@homeward-music.com"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="contact-phone">Kontakt-Telefon</label>
          <input
            type="text"
            id="contact-phone"
            v-model="siteSettings.contact_phone"
            placeholder="+49 123 456 7890"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="contact-location">Kontakt-Standort</label>
          <input
            type="text"
            id="contact-location"
            v-model="siteSettings.contact_location"
            placeholder="München, Deutschland"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="footer-text">Footer-Text</label>
          <input
            type="text"
            id="footer-text"
            v-model="siteSettings.footer_text"
            placeholder="z.B. © HomeWard Irish Folk Band"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="facebook-url">Facebook URL</label>
          <input
            type="text"
            id="facebook-url"
            v-model="siteSettings.facebook_url"
            placeholder="z.B. https://www.facebook.com/homewardmusic"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="youtube-url">YouTube URL</label>
          <input
            type="text"
            id="youtube-url"
            v-model="siteSettings.youtube_url"
            placeholder="z.B. https://www.youtube.com/homewardmusic"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="tiktok-url">TikTok URL</label>
          <input
            type="text"
            id="tiktok-url"
            v-model="siteSettings.tiktok_url"
            placeholder="z.B. https://www.tiktok.com/@homewardmusic"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="instagram-url">Instagram URL</label>
          <input
            type="text"
            id="instagram-url"
            v-model="siteSettings.instagram_url"
            placeholder="z.B. https://www.instagram.com/homeward_folkband"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="hero-image">Hero-Bild</label>
          <div class="image-upload-container">
            <div v-if="siteSettings.hero_image" class="image-preview">
              <img :src="siteSettings.hero_image" alt="Hero-Bild" />
            </div>
            <div v-else-if="heroImagePreview" class="image-preview">
              <img :src="heroImagePreview" alt="Hero-Bild Vorschau" />
            </div>
            <div v-else class="no-image">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p>Kein Bild gewählt</p>
            </div>
            
            <div class="image-upload-actions">
              <label for="hero-image-upload" class="btn-upload">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                Bild hochladen
              </label>
              <input
                type="file"
                id="hero-image-upload"
                @change="handleHeroImageUpload"
                class="file-input"
                accept="image/*"
              />
              <button 
                v-if="selectedHeroImage" 
                @click="uploadHeroImage" 
                class="btn-save-image"
                :disabled="isUploading"
              >
                <span v-if="isUploading">Wird hochgeladen...</span>
                <span v-else>Speichern</span>
              </button>
            </div>
          </div>
        </div>
        
        <div class="form-group">
          <label for="logo-image">Logo</label>
          <div class="image-upload-container">
            <div v-if="siteSettings.logo_url" class="image-preview">
              <img :src="siteSettings.logo_url" alt="Logo" />
            </div>
            <div v-else-if="logoImagePreview" class="image-preview">
              <img :src="logoImagePreview" alt="Logo Vorschau" />
            </div>
            <div v-else class="no-image">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p>Kein Logo gewählt</p>
            </div>
            
            <div class="image-upload-actions">
              <label for="logo-image-upload" class="btn-upload">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                Logo hochladen
              </label>
              <input
                type="file"
                id="logo-image-upload"
                @change="handleLogoImageUpload"
                class="file-input"
                accept="image/*"
              />
              <button 
                v-if="selectedLogoImage" 
                @click="uploadLogoImage" 
                class="btn-save-image"
                :disabled="isUploading"
              >
                <span v-if="isUploading">Wird hochgeladen...</span>
                <span v-else>Speichern</span>
              </button>
            </div>
          </div>
        </div>
        
        <div class="form-group">
          <label for="favicon-image">Favicon</label>
          <div class="image-upload-container">
            <div v-if="siteSettings.favicon_url" class="image-preview favicon-preview">
              <img :src="siteSettings.favicon_url" alt="Favicon" />
            </div>
            <div v-else-if="faviconImagePreview" class="image-preview favicon-preview">
              <img :src="faviconImagePreview" alt="Favicon Vorschau" />
            </div>
            <div v-else class="no-image">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p>Kein Favicon gewählt</p>
            </div>
            
            <div class="image-upload-actions">
              <label for="favicon-image-upload" class="btn-upload">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                Favicon hochladen
              </label>
              <input
                type="file"
                id="favicon-image-upload"
                @change="handleFaviconImageUpload"
                class="file-input"
                accept="image/*"
              />
              <button 
                v-if="selectedFaviconImage" 
                @click="uploadFaviconImage" 
                class="btn-save-image"
                :disabled="isUploading"
              >
                <span v-if="isUploading">Wird hochgeladen...</span>
                <span v-else>Speichern</span>
              </button>
            </div>
            <p class="help-text">Empfohlen: Eine quadratische Bilddatei (z.B. PNG) mit 32×32 oder 48×48 Pixeln.</p>
          </div>
        </div>
        
        <!-- SEO Einstellungen -->
        <h4 class="settings-subheader">SEO Einstellungen</h4>
        
        <div class="form-group">
          <label for="meta-title">Meta Titel</label>
          <input
            type="text"
            id="meta-title"
            v-model="siteSettings.meta_title"
            placeholder="Seitentitel für Suchmaschinen"
            class="form-control"
          />
          <p class="help-text">Der Titel, der in Suchmaschinen und im Browser-Tab angezeigt wird.</p>
        </div>
        
        <div class="form-group">
          <label for="meta-description">Meta Beschreibung</label>
          <textarea
            id="meta-description"
            v-model="siteSettings.meta_description"
            placeholder="Kurze Beschreibung der Website für Suchmaschinen"
            class="form-control"
            rows="3"
          ></textarea>
          <p class="help-text">Eine kurze Beschreibung (ca. 150-160 Zeichen), die in Suchergebnissen angezeigt wird.</p>
        </div>
        
        <div class="form-group">
          <label for="meta-keywords">Meta Keywords</label>
          <input
            type="text"
            id="meta-keywords"
            v-model="siteSettings.meta_keywords"
            placeholder="z.B. Folkband, Musik, Konzerte, München"
            class="form-control"
          />
          <p class="help-text">Kommagetrennte Liste von Schlüsselwörtern, die Ihre Website beschreiben.</p>
        </div>
        
        <div class="form-actions">
          <button type="submit" class="btn-save" :disabled="loading">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            Einstellungen speichern
          </button>
        </div>
      </form>
    </div>
    
    <!-- Nachrichten (Erfolg, Fehler, Ladevorgang) -->
    <div v-if="loading" class="loading-spinner">
      <div class="spinner"></div>
      <span>Wird geladen...</span>
    </div>
    
    <div v-if="error" class="error-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>{{ error }}</span>
    </div>
    
    <div v-if="successMessage" class="success-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
  </div>
</template>

<style scoped>
.settings-manager {
  max-width: 800px;
  margin: 0 auto;
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.section-description {
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.5;
}

.settings-card {
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  margin-bottom: 2rem;
  position: relative;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background-color: rgba(0, 132, 61, 0.1);
  border-radius: 12px;
  color: var(--primary-color);
}

.card-header h3 {
  font-size: 1.25rem;
  margin: 0 0 0.25rem 0;
  color: var(--text-color);
}

.card-header p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.95rem;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background-color: var(--bg-alt);
  border-radius: 4px;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-indicator.connected {
  background-color: #4CAF50;
}

.status-indicator.disconnected {
  background-color: #F44336;
}

.connection-details {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: var(--bg-alt);
  border-radius: 4px;
}

.detail-row {
  display: flex;
  margin-bottom: 0.5rem;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  min-width: 150px;
  color: var(--text-muted);
}

.detail-value {
  font-weight: 500;
}

.card-actions {
  display: flex;
  gap: 1rem;
}

.btn-connect, .btn-disconnect, .btn-save {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  border-radius: 4px;
  border: none;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-normal);
}

.btn-connect, .btn-save {
  background-color: var(--primary-color);
  color: white;
}

.btn-connect:hover, .btn-save:hover {
  background-color: var(--primary-color-dark);
}

.btn-disconnect {
  background-color: #e53935;
  color: white;
}

.btn-disconnect:hover {
  background-color: #d32f2f;
}

.btn-icon {
  display: flex;
}

.loading-spinner {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: var(--text-color);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s infinite linear;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message, .success-message {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: fadeInOut 5s ease-in-out;
}

.error-message {
  background-color: rgba(244, 67, 54, 0.95);
  color: white;
}

.success-message {
  background-color: rgba(76, 175, 80, 0.95);
  color: white;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, -40%); }
  10% { opacity: 1; transform: translate(-50%, -50%); }
  90% { opacity: 1; transform: translate(-50%, -50%); }
  100% { opacity: 0; transform: translate(-50%, -60%); }
}

/* Formular-Stile */
.settings-form {
  margin-top: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.form-control {
  width: 100%;
  padding: 0.65rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: var(--primary-color);
  outline: none;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
}

.form-toggle {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.toggle-description {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.toggle-switch {
  position: relative;
  width: 50px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-label {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 34px;
}

.toggle-label:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .toggle-label {
  background-color: var(--primary-color);
}

input:checked + .toggle-label:before {
  transform: translateX(26px);
}

.hero-image-upload {
  margin-top: 1rem;
}

.image-preview {
  width: 100%;
  max-width: 400px;
  height: 225px;
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f5f5;
  border: 2px dashed #ddd;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.file-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.btn-upload {
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-upload:hover:not(:disabled) {
  background-color: var(--primary-color-dark);
}

.btn-upload:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.help-text {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.logo-image-upload {
  margin-top: 1rem;
}

.favicon-image-upload {
  margin-top: 1rem;
}

.image-upload-container {
  margin-top: 1rem;
}

.image-upload-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.btn-save-image {
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-save-image:hover:not(:disabled) {
  background-color: var(--primary-color-dark);
}

.btn-save-image:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border: 2px dashed #ddd;
  border-radius: 8px;
  text-align: center;
}

.no-image svg {
  margin-bottom: 0.5rem;
}

.no-image p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.settings-subheader {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--text-color);
}
</style>

 